<template>
  <div class="trunk-costs-admin">
    <h1>Транки и цены</h1>

    <p class="table-description">
      Пустое поле использует значение по умолчанию. Значение 0 считается
      настоящей ценой.
    </p>

    <div v-if="loading" class="state-message">Загрузка транков...</div>

    <div v-else-if="loadError" class="load-error">
      {{ loadError }}
    </div>

    <div v-else class="trunks-list">
      <div
        v-for="trunkItem in trunks"
        :key="trunkItem.trunk"
        class="trunk-card"
        :class="{ 'inactive-card': !trunkItem.active }"
      >
        <button
          class="trunk-header"
          type="button"
          @click="trunkItem.expanded = !trunkItem.expanded"
        >
          <span class="trunk-name">
            {{ trunkItem.trunk }}
          </span>

          <span
            class="trunk-status"
            :class="trunkItem.active ? 'active' : 'inactive'"
          >
            {{ trunkItem.active ? 'Активен' : 'Неактивен' }}
          </span>

          <span class="expand-icon" :class="{ expanded: trunkItem.expanded }">
            ▼
          </span>
        </button>

        <div v-if="trunkItem.expanded" class="trunk-content">
          <section class="price-section">
            <h2>Номер</h2>

            <div class="number-prices">
              <label class="price-field">
                <span>Минимальная цена, ₽</span>

                <input
                  v-model="trunkItem.prices.number.cost"
                  type="number"
                  min="0"
                  step="0.01"
                  :placeholder="String(defaults.number.cost)"
                  :disabled="!trunkItem.editing || trunkItem.saving"
                />

                <small v-if="trunkItem.prices.number.cost === ''">
                  По умолчанию: {{ defaults.number.cost }} ₽
                </small>
              </label>

              <label class="price-field">
                <span>Стандартная цена, ₽</span>

                <input
                  v-model="trunkItem.prices.number.standard"
                  type="number"
                  min="0"
                  step="0.01"
                  :placeholder="String(defaults.number.standard)"
                  :disabled="!trunkItem.editing || trunkItem.saving"
                />

                <small v-if="trunkItem.prices.number.standard === ''">
                  По умолчанию: {{ defaults.number.standard }} ₽
                </small>
              </label>
            </div>
          </section>

          <section class="price-section">
            <h2>Инициации</h2>

            <div class="initiation-table">
              <div class="initiation-row initiation-header">
                <div>Оператор</div>
                <div>Минимальная цена, ₽</div>
                <div>Стандартная цена, ₽</div>
              </div>

              <div
                v-for="operator in operators"
                :key="operator.key"
                class="initiation-row"
              >
                <div class="operator-name">
                  {{ operator.label }}
                </div>

                <label class="price-field">
                  <input
                    v-model="trunkItem.prices.initiation[operator.key].cost"
                    type="number"
                    min="0"
                    step="0.01"
                    :placeholder="String(defaults.initiation.cost)"
                    :disabled="!trunkItem.editing || trunkItem.saving"
                  />

                  <small
                    v-if="trunkItem.prices.initiation[operator.key].cost === ''"
                  >
                    По умолчанию: {{ defaults.initiation.cost }} ₽
                  </small>
                </label>

                <label class="price-field">
                  <input
                    v-model="trunkItem.prices.initiation[operator.key].standard"
                    type="number"
                    min="0"
                    step="0.01"
                    :placeholder="String(defaults.initiation.standard)"
                    :disabled="!trunkItem.editing || trunkItem.saving"
                  />

                  <small
                    v-if="
                      trunkItem.prices.initiation[operator.key].standard === ''
                    "
                  >
                    По умолчанию: {{ defaults.initiation.standard }} ₽
                  </small>
                </label>
              </div>
            </div>
          </section>

          <div class="trunk-actions">
            <button
              v-if="!trunkItem.editing"
              class="edit-button"
              type="button"
              @click="startEditing(trunkItem)"
            >
              Редактировать
            </button>

            <template v-else>
              <button
                class="cancel-button"
                type="button"
                :disabled="trunkItem.saving"
                @click="cancelEditing(trunkItem)"
              >
                Отменить
              </button>

              <button
                class="save-button"
                type="button"
                :disabled="trunkItem.saving"
                @click="saveTrunkPrices(trunkItem)"
              >
                {{ trunkItem.saving ? 'Сохранение...' : 'Сохранить' }}
              </button>
            </template>

            <span
              v-if="trunkItem.saveStatus === 'success'"
              class="save-success"
            >
              Сохранено
            </span>

            <span v-if="trunkItem.saveStatus === 'error'" class="save-error">
              {{ trunkItem.saveError }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <v-dialog v-model="standardConfirmation.show" max-width="520" persistent>
      <v-card class="reset-price-modal">
        <button
          class="reset-price-modal__close"
          type="button"
          @click="closeStandardConfirmation"
        >
          ×
        </button>

        <div class="reset-price-modal__title">Изменить стандартную цену?</div>

        <div class="reset-price-modal__text">
          Новая стандартная цена будет применена ко всем пользователям,
          использующим режим
          <strong class="standard-price-modal__accent"> «Стандартная» </strong>.
        </div>

        <div class="reset-price-modal__hint">
          Индивидуальные цены пользователей не изменятся.
        </div>

        <div class="reset-price-modal__actions">
          <button
            class="reset-price-modal__button reset-price-modal__button--cancel"
            type="button"
            @click="closeStandardConfirmation"
          >
            Отмена
          </button>

          <button
            class="reset-price-modal__button reset-price-modal__button--confirm"
            type="button"
            @click="confirmStandardPriceChange"
          >
            Продолжить
          </button>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="resetConfirmation.show" max-width="520" persistent>
      <v-card class="reset-price-modal">
        <button
          class="reset-price-modal__close"
          type="button"
          @click="closeResetConfirmation"
        >
          ×
        </button>

        <div class="reset-price-modal__title">
          Сбросить индивидуальные цены?
        </div>

        <div class="reset-price-modal__text">
          Новая минимальная цена стала выше некоторых индивидуальных цен.
        </div>

        <div class="reset-price-modal__warning">
          Пользователи будут переключены на стандартный режим:
          <strong>
            {{ resetConfirmation.affectedUsers }}
          </strong>
        </div>

        <div class="reset-price-modal__hint">
          Удалённые индивидуальные цены автоматически не восстановятся, даже
          если минимальная цена позднее уменьшится.
        </div>

        <div class="reset-price-modal__actions">
          <button
            class="reset-price-modal__button reset-price-modal__button--cancel"
            type="button"
            @click="closeResetConfirmation"
          >
            Отмена
          </button>

          <button
            class="reset-price-modal__button reset-price-modal__button--confirm"
            type="button"
            @click="confirmResetIndividualPrices"
          >
            Продолжить
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'TrunkCostsAdmin',

  data() {
    return {
      trunks: [],

      operators: [
        {
          key: 'mts',
          label: 'МТС',
        },
        {
          key: 'beeline',
          label: 'Билайн',
        },
        {
          key: 'megafon',
          label: 'Мегафон',
        },
        {
          key: 'yota',
          label: 'Yota',
        },
        {
          key: 't2',
          label: 'Т2',
        },
        {
          key: 'other',
          label: 'Остальные',
        },
      ],

      defaults: {
        number: {
          cost: 183,
          standard: 200,
        },
        initiation: {
          cost: 0.61,
          standard: 0.67,
        },
      },

      loading: false,
      loadError: '',
      standardConfirmation: {
        show: false,
        trunkItem: null,
      },
      resetConfirmation: {
        show: false,
        trunkItem: null,
        affectedUsers: 0,
        resets: [],
        standardConfirmed: false,
      },
    };
  },

  mounted() {
    this.loadTrunks();
  },

  methods: {
    async loadTrunks() {
      this.loading = true;
      this.loadError = '';

      try {
        const response = await this.$http.get('/statistics/get-billing-trunks');

        if (response.data.defaults) {
          this.defaults = response.data.defaults;
        }

        const trunks = Array.isArray(response.data.trunks)
          ? response.data.trunks
          : [];

        const normalizePriceFields = (positionPrices) => {
          const source = positionPrices || {};

          return {
            cost: source.cost !== undefined ? source.cost : '',
            standard: source.standard !== undefined ? source.standard : '',
          };
        };
        this.trunks = trunks.map((trunkItem) => {
          const prices = trunkItem.prices || {};
          const initiation = prices.initiation || {};

          return {
            trunk: trunkItem.trunk,
            active: trunkItem.active,
            expanded: false,
            editing: false,
            originalPrices: null,

            saving: false,
            saveStatus: '',
            saveError: '',

            prices: {
              number: normalizePriceFields(prices.number),

              initiation: {
                mts: normalizePriceFields(initiation.mts),
                beeline: normalizePriceFields(initiation.beeline),
                megafon: normalizePriceFields(initiation.megafon),
                yota: normalizePriceFields(initiation.yota),
                t2: normalizePriceFields(initiation.t2),
                other: normalizePriceFields(initiation.other),
              },
            },
          };
        });
      } catch (error) {
        console.error('Ошибка загрузки транков и себестоимости:', error);

        this.loadError = 'Не удалось загрузить список транков';
      } finally {
        this.loading = false;
      }
    },

    clonePrices(prices) {
      return JSON.parse(JSON.stringify(prices));
    },

    startEditing(trunkItem) {
      trunkItem.originalPrices = this.clonePrices(trunkItem.prices);
      trunkItem.editing = true;
      trunkItem.saveStatus = '';
      trunkItem.saveError = '';
    },

    cancelEditing(trunkItem) {
      trunkItem.prices = this.clonePrices(trunkItem.originalPrices);
      trunkItem.originalPrices = null;
      trunkItem.editing = false;
      trunkItem.saveStatus = '';
      trunkItem.saveError = '';
    },

    closeStandardConfirmation() {
      this.standardConfirmation = {
        show: false,
        trunkItem: null,
      };
    },

    confirmStandardPriceChange() {
      const trunkItem = this.standardConfirmation.trunkItem;

      this.closeStandardConfirmation();

      if (trunkItem) {
        this.saveTrunkPrices(trunkItem, false, true);
      }
    },

    closeResetConfirmation() {
      this.resetConfirmation = {
        show: false,
        trunkItem: null,
        affectedUsers: 0,
        resets: [],
        standardConfirmed: false,
      };
    },

    confirmResetIndividualPrices() {
      const trunkItem = this.resetConfirmation.trunkItem;
      const standardConfirmed = this.resetConfirmation.standardConfirmed;

      this.closeResetConfirmation();

      if (trunkItem) {
        this.saveTrunkPrices(trunkItem, true, standardConfirmed);
      }
    },

    hasStandardPriceChanges(trunkItem) {
      const original = trunkItem.originalPrices || {};
      const current = trunkItem.prices || {};

      const effectiveValue = (value, fallback) => {
        if (value === '' || value === null || typeof value === 'undefined') {
          return fallback;
        }

        return Number(String(value).replace(',', '.'));
      };

      const positions = [
        [original.number?.standard, current.number?.standard, 200],
        ...['mts', 'beeline', 'megafon', 'yota', 't2', 'other'].map(
          (operator) => [
            original.initiation?.[operator]?.standard,
            current.initiation?.[operator]?.standard,
            0.67,
          ],
        ),
      ];

      return positions.some(
        ([oldValue, newValue, fallback]) =>
          effectiveValue(oldValue, fallback) !==
          effectiveValue(newValue, fallback),
      );
    },

    saveTrunkPrices(
      trunkItem,
      confirmIndividualReset = false,
      standardConfirmed = false,
    ) {
      if (!standardConfirmed && this.hasStandardPriceChanges(trunkItem)) {
        this.standardConfirmation = {
          show: true,
          trunkItem,
        };

        return;
      }
      trunkItem.saving = true;
      trunkItem.saveStatus = '';
      trunkItem.saveError = '';

      this.$http.post(
        '/statistics/save-billing-trunk-prices',
        {
          trunk: trunkItem.trunk,
          prices: trunkItem.prices,
          confirmIndividualReset,
        },
        (result) => {
          trunkItem.saving = false;
          if (result && result.requiresConfirmation) {
            this.resetConfirmation = {
              show: true,
              trunkItem,
              affectedUsers: result.affectedUsers || 0,
              resets: Array.isArray(result.resets) ? result.resets : [],
              standardConfirmed,
            };

            return;
          }

          if (result && result.success) {
            trunkItem.prices = this.clonePrices(result.prices);
            trunkItem.originalPrices = null;
            trunkItem.editing = false;
            trunkItem.saveStatus = 'success';

            setTimeout(() => {
              trunkItem.saveStatus = '';
            }, 2000);

            return;
          }

          trunkItem.saveStatus = 'error';
          trunkItem.saveError =
            result && result.error ? result.error : 'Не удалось сохранить';
        },
      );
    },
  },
};
</script>

<style scoped>
.trunk-costs-admin {
  padding: 24px;
}

.trunk-costs-admin h1 {
  margin: 0;
  color: #343840;
}

.table-description {
  margin: 8px 0 18px;
  color: #7a8190;
  font-size: 14px;
}

.state-message,
.load-error {
  margin-top: 20px;
}

.load-error {
  color: #de4040;
}

.trunks-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.trunk-card {
  overflow: hidden;
  border: 1px solid #e1e5eb;
  border-radius: 8px;
  background: #ffffff;
}

.inactive-card {
  background: #fafafa;
}

.trunk-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 24px;
  gap: 14px;
  align-items: center;
  width: 100%;
  padding: 14px 16px;
  border: none;
  background: transparent;
  color: #344054;
  text-align: left;
  cursor: pointer;
}

.trunk-header:hover {
  background: #f7f8fa;
}

.trunk-name {
  overflow: hidden;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trunk-status {
  font-size: 13px;
}

.active {
  color: #238636;
}

.inactive {
  color: #de4040;
}

.expand-icon {
  color: #7a8190;
  font-size: 11px;
  text-align: center;
  transition: transform 0.2s ease;
}

.expand-icon.expanded {
  transform: rotate(180deg);
}

.trunk-content {
  padding: 0 16px 16px;
  border-top: 1px solid #e7eaf0;
}

.price-section {
  width: 100%;
  max-width: 1000px;
  margin-right: auto;
  margin-left: auto;
  padding-top: 18px;
}

.price-section + .price-section {
  margin-top: 18px;
  border-top: 1px solid #edf0f4;
}

.price-section h2 {
  margin: 0 0 12px;
  color: #344054;
  font-size: 16px;
  text-align: center;
}

.number-prices {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 300px));
  justify-content: center;
  gap: 16px;
}

.price-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  color: #4b5565;
  font-size: 13px;
}

.price-field input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #d9dde5;
  border-radius: 5px;
  outline: none;
  font-size: 14px;
}

.price-field input:focus {
  border-color: #4b5eff;
}

.price-field input:disabled {
  background: #f7f8fa;
  color: #667085;
  cursor: default;
}

.price-field input::placeholder {
  color: #a1a7b3;
}

.price-field small {
  min-height: 14px;
  color: #8a91a0;
  font-size: 11px;
}

.initiation-table {
  overflow: hidden;
  width: 100%;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
}

.initiation-row {
  display: grid;
  grid-template-columns: 160px minmax(180px, 1fr) minmax(180px, 1fr);
}

.initiation-row + .initiation-row {
  border-top: 1px solid #e4e7ec;
}

.initiation-row > div,
.initiation-row > label {
  padding: 10px;
}

.initiation-row > * + * {
  border-left: 1px solid #e4e7ec;
}

.initiation-header {
  background: #f7f8fa;
  color: #344054;
  font-size: 13px;
  font-weight: 600;
}

.operator-name {
  display: flex;
  align-items: center;
  color: #42526a;
  font-size: 14px;
}

.trunk-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
  max-width: 1000px;
  margin: 18px auto 0;
}

.edit-button,
.cancel-button,
.save-button {
  padding: 8px 14px;
  border: 1px solid transparent;
  border-radius: 5px;
  cursor: pointer;
}

.edit-button {
  border-color: #4b5eff;
  background: #ffffff;
  color: #4b5eff;
}

.edit-button:hover {
  background: #f3f5ff;
}

.cancel-button {
  border-color: #d0d5dd;
  background: #ffffff;
  color: #475467;
}

.cancel-button:hover {
  background: #f7f8fa;
}

.save-button {
  background: #4b5eff;
  color: #ffffff;
}

.edit-button:disabled,
.cancel-button:disabled,
.save-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.save-success {
  color: #238636;
  font-size: 13px;
}

.save-error {
  color: #de4040;
  font-size: 13px;
}

.reset-price-modal {
  position: relative;
  padding: 30px;
  border-radius: 10px;
  font-family: Manrope, system-ui, sans-serif;
}

.reset-price-modal__close {
  position: absolute;
  top: 12px;
  right: 14px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: #98a2b3;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.reset-price-modal__close:hover {
  color: #344054;
}

.reset-price-modal__title {
  padding-right: 30px;
  color: #1d2939;
  font-size: 20px;
  font-weight: 600;
}

.reset-price-modal__text {
  margin-top: 14px;
  color: #475467;
  font-size: 14px;
  line-height: 1.5;
}

.reset-price-modal__warning {
  margin-top: 16px;
  padding: 12px 14px;
  border: 1px solid #f4c96b;
  border-radius: 6px;
  background: #fff8e7;
  color: #7a4d00;
  font-size: 14px;
}

.reset-price-modal__hint {
  margin-top: 12px;
  color: #667085;
  font-size: 13px;
  line-height: 1.45;
}

.reset-price-modal__actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 24px;
}

.reset-price-modal__button {
  padding: 9px 16px;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
}

.reset-price-modal__button--cancel {
  border-color: #d0d5dd;
  background: #ffffff;
  color: #475467;
}

.reset-price-modal__button--cancel:hover {
  background: #f7f8fa;
}

.reset-price-modal__button--confirm {
  background: #d92d20;
  color: #ffffff;
}

.reset-price-modal__button--confirm:hover {
  background: #b42318;
}

.standard-price-modal__accent {
  color: #4b5eff;
  font-weight: 600;
}

@media (max-width: 760px) {
  .trunk-costs-admin {
    padding: 16px;
  }

  .number-prices {
    grid-template-columns: 1fr;
  }

  .initiation-table {
    overflow-x: auto;
  }

  .initiation-row {
    min-width: 620px;
  }
}
</style>
